import React, { useEffect, useRef } from 'react';
import './styles.css';
import { useModalFocusReturn } from '../../hooks/useModalFocusReturn';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

const ExternalLinkModal = ({ isOpen, onConfirm, onCancel, url }) => {
    const modalRef = useRef(null);

    // Restore focus to whatever triggered this modal once it closes (declared before
    // the effect below so it captures the trigger element BEFORE that effect moves
    // focus onto the first focusable element inside the modal).
    useModalFocusReturn(isOpen);

    // Lock page scroll while open, without jumping to the top and correctly restoring
    // the exact scroll position on close.
    useBodyScrollLock(isOpen);

    useEffect(() => {
        if (isOpen && modalRef.current) {
            const focusableElements = modalRef.current.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            const handleTabKey = (e) => {
                if (e.key === 'Tab') {
                    if (e.shiftKey && document.activeElement === firstElement) {
                        e.preventDefault();
                        lastElement?.focus();
                    } else if (!e.shiftKey && document.activeElement === lastElement) {
                        e.preventDefault();
                        firstElement?.focus();
                    }
                }
            };

            const handleEscapeKey = (e) => {
                if (e.key === 'Escape') {
                    onCancel();
                }
            };

            modalRef.current.addEventListener('keydown', handleTabKey);
            document.addEventListener('keydown', handleEscapeKey);

            // Focus first button
            firstElement?.focus({ preventScroll: true });

            return () => {
                modalRef.current?.removeEventListener('keydown', handleTabKey);
                document.removeEventListener('keydown', handleEscapeKey);
            };
        }
    }, [isOpen, onCancel]);

    const handleOverlayClick = (e) => {
        if (e.target.classList.contains('external-link-modal-overlay')) {
            onCancel();
        }
    };

    if (!isOpen) return null;

    return (
        <div 
            className="external-link-modal-overlay"
            onClick={handleOverlayClick}
            role="dialog"
            aria-modal="true"
            aria-labelledby="external-link-modal-title"
        >
            <div className="external-link-modal" ref={modalRef}>
                <button 
                    className="external-link-modal-close"
                    onClick={onCancel}
                    aria-label="Close modal"
                    type="button"
                >
                    ×
                </button>
                
                <div className="external-link-modal-content">
                    <h2 id="external-link-modal-title" className="external-link-modal-title">
                        You are now leaving Aetna Better Health®.
                    </h2>
                    
                    <p className="external-link-modal-message">
                        If you continue, you'll leave Aetna Better Health. Other websites may have different terms and privacy practices.
                    </p>
                </div>

                <div className="external-link-modal-footer">
                    <button 
                        className="external-link-modal-button external-link-modal-button-primary"
                        onClick={onConfirm}
                        type="button"
                    >
                        Go on
                    </button>
                    <button 
                        className="external-link-modal-button external-link-modal-button-secondary"
                        onClick={onCancel}
                        type="button"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ExternalLinkModal;
